import type { IntlShape } from 'react-intl';

import { StatusType } from '~/eds/src/types/StatusType';
import { UploadStatusType } from '~/enums/UploadStatusType';
import { capitalize, toLowercaseTrimmed } from '~/utils/strings';

import type { AlgoStatus, BaseStatusMessageProps, UploadFile } from './types';

export const getChipStatus = (d: UploadFile, intl: IntlShape) => {
  let text;
  let status;
  let icon;
  let tooltip;

  switch (d.status) {
    case UploadStatusType.Completed:
      icon = 'status-complete';
      text = intl.formatMessage({
        id: 'Utils.getChipStatus.completed',
        defaultMessage: 'Completed',
        description: 'Status text for completed upload',
      });
      tooltip = intl.formatMessage({
        id: 'Utils.getChipStatus.completedTooltip',
        defaultMessage: 'Your document was uploaded and processed successfully',
        description: 'Tooltip for completed upload status',
      });
      status = 'success';
      break;
    case UploadStatusType.Queued:
      icon = 'clock';
      text = intl.formatMessage({
        id: 'Utils.getChipStatus.queued',
        defaultMessage: 'Queued',
        description: 'Status text for queued upload',
      });
      tooltip = intl.formatMessage({
        id: 'Utils.getChipStatus.queuedTooltip',
        defaultMessage:
          'Your document was uploaded successfully and has been placed in line for processing, which may take several minutes',
        description: 'Tooltip for queued upload status',
      });
      status = 'warning';
      break;
    case UploadStatusType.Processing:
      icon = 'sync';
      text = intl.formatMessage({
        id: 'Utils.getChipStatus.processing',
        defaultMessage: 'Processing',
        description: 'Status text for processing upload',
      });
      tooltip = intl.formatMessage({
        id: 'Utils.getChipStatus.processingTooltip',
        defaultMessage:
          'Your document is being processed and should be available shortly',
        description: 'Tooltip for processing upload status',
      });
      status = 'active';
      break;
    case UploadStatusType.UploadFileTypeFailure:
      icon = 'x';
      text = intl.formatMessage({
        id: 'Utils.getChipStatus.uploadFailed',
        defaultMessage: 'Upload Failed',
        description: 'Status text for failed upload',
      });
      tooltip = intl.formatMessage({
        id: 'Utils.getChipStatus.uploadFailedTooltip',
        defaultMessage:
          'Your document was not uploaded because its file type is not supported.',
        description: 'Tooltip for failed upload due to file type',
      });
      status = 'danger';
      break;
    case UploadStatusType.ProcessingError:
    case UploadStatusType.UploadFailed:
    case UploadStatusType.OcrFailure:
    case UploadStatusType.AlgorithmFailure:
      icon = 'x';
      text = intl.formatMessage({
        id: 'Utils.getChipStatus.processingError',
        defaultMessage: 'Processing Error',
        description: 'Status text for processing error',
      });
      tooltip = intl.formatMessage({
        id: 'Utils.getChipStatus.processingErrorTooltip',
        defaultMessage:
          'Your document was not processed due to a system error. Please try uploading again later or contact support.',
        description: 'Tooltip for processing error',
      });
      status = 'danger';
      break;
    case UploadStatusType.Uploading:
      icon = 'arrow-up';
      text = intl.formatMessage({
        id: 'Utils.getChipStatus.uploading',
        defaultMessage: 'Uploading',
        description: 'Status text for uploading',
      });
      tooltip = intl.formatMessage({
        id: 'Utils.getChipStatus.uploadingTooltip',
        defaultMessage: 'Your document is uploading',
        description: 'Tooltip for uploading status',
      });
      status = 'active';
      break;
    case UploadStatusType.Duplicate:
      icon = 'status-warning';
      text = intl.formatMessage({
        id: 'Utils.getChipStatus.duplicate',
        defaultMessage: 'Duplicate',
        description: 'Status text for duplicate upload',
      });
      status = 'warning';
      break;
    default:
      status = 'info';
      text = intl.formatMessage({
        id: 'Utils.getChipStatus.unknownStatus',
        defaultMessage: 'Unknown status',
        description: 'Status text for unknown status',
      });
      tooltip = undefined;
  }

  return { text, status, icon, tooltip };
};

export const formatUploadSize = (d: UploadFile, intl: IntlShape) => {
  const size = [
    UploadStatusType.Uploading,
    UploadStatusType.UploadFailed,
  ].includes(d.status)
    ? d.size
    : d.size * 1024;

  const units = [
    intl.formatMessage({
      id: 'Utils.formatUploadSize.bytesUnit',
      defaultMessage: 'B',
      description: 'Bytes unit abbreviation',
    }),
    intl.formatMessage({
      id: 'Utils.formatUploadSize.kilobytesUnit',
      defaultMessage: 'KB',
      description: 'Kilobytes unit abbreviation',
    }),
    intl.formatMessage({
      id: 'Utils.formatUploadSize.megabytesUnit',
      defaultMessage: 'MB',
      description: 'Megabytes unit abbreviation',
    }),
    intl.formatMessage({
      id: 'Utils.formatUploadSize.gigabytesUnit',
      defaultMessage: 'GB',
      description: 'Gigabytes unit abbreviation',
    }),
    intl.formatMessage({
      id: 'Utils.formatUploadSize.terabytesUnit',
      defaultMessage: 'TB',
      description: 'Terabytes unit abbreviation',
    }),
    intl.formatMessage({
      id: 'Utils.formatUploadSize.petabytesUnit',
      defaultMessage: 'PB',
      description: 'Petabytes unit abbreviation',
    }),
    intl.formatMessage({
      id: 'Utils.formatUploadSize.exabytesUnit',
      defaultMessage: 'EB',
      description: 'Exabytes unit abbreviation',
    }),
    intl.formatMessage({
      id: 'Utils.formatUploadSize.zettabytesUnit',
      defaultMessage: 'ZB',
      description: 'Zettabytes unit abbreviation',
    }),
    intl.formatMessage({
      id: 'Utils.formatUploadSize.yottabytesUnit',
      defaultMessage: 'YB',
      description: 'Yottabytes unit abbreviation',
    }),
  ];
  const i = Math.floor(Math.log(size) / Math.log(1024));
  const unit = units[i];

  const amount = (size / Math.pow(1024, i)).toFixed(2);

  return { amount, unit };
};

export const isFileUploading = (status: UploadStatusType) => {
  switch (status) {
    case UploadStatusType.Uploading:
    case UploadStatusType.UploadFailed:
    case UploadStatusType.ProcessingError:
      return true;
    default:
      return false;
  }
};

export const formatLabel = (label: string) => {
  return capitalize(toLowercaseTrimmed(label));
};

export const mapAlgoStatusMessageProps = (
  algoStatus: AlgoStatus,
  intl: IntlShape,
): BaseStatusMessageProps => {
  const { title, status, message } = algoStatus;

  let statusMessage;
  let statusColor: StatusType;
  switch (status) {
    case 0:
      statusColor = 'success';
      statusMessage = intl.formatMessage({
        id: 'Utils.mapAlgoStatusMessageProps.inProgress',
        defaultMessage: 'In Progress',
        description: 'Status message for in progress algorithm',
      });
      break;
    case -1:
      statusColor = 'danger';
      statusMessage = intl.formatMessage({
        id: 'Utils.mapAlgoStatusMessageProps.failed',
        defaultMessage: 'Failed',
        description: 'Status message for failed algorithm',
      });
      break;
    case 1:
      statusColor = 'success';
      break;
    case -2:
      statusColor = 'active';
      statusMessage = intl.formatMessage({
        id: 'Utils.mapAlgoStatusMessageProps.partiallyComplete',
        defaultMessage: 'Partially Complete',
        description: 'Status message for partially complete algorithm',
      });
      break;
    default:
      statusColor = 'warning';
      statusMessage = message;
      break;
  }
  return {
    message: statusMessage ?? '',
    status: statusColor,
    title,
  };
};
